import { useEffect, useState, type JSX } from 'react';
import { ProductCard } from '@/components/ui';
import { fetchProductDataById } from '@/services/slaydAPI/products';
import type { Product } from '@/types/Product.types';
import styles from './ProductFeedPage.module.css';

const STORAGE_KEY = 'productFeedPage_state';

interface ProductFetchResult {
  id: string;
  status: 'loading' | 'success' | 'error';
  product?: Product;
  error?: string;
}

const parseIds = (raw: string): string[] => {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

const ProductFeedPage = (): JSX.Element => {
  const [inputValue, setInputValue] = useState<string>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved)
        return (JSON.parse(saved) as { inputValue: string }).inputValue ?? '';
    } catch {
      // ignore parse errors
    }
    return '';
  });
  const [results, setResults] = useState<ProductFetchResult[]>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved)
        return (
          (JSON.parse(saved) as { results: ProductFetchResult[] }).results ?? []
        );
    } catch {
      // ignore parse errors
    }
    return [];
  });
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ inputValue, results })
      );
    } catch {
      // ignore storage errors (e.g. private browsing quota)
    }
  }, [inputValue, results]);

  const handleGenerate = async (): Promise<void> => {
    const ids = parseIds(inputValue);
    if (ids.length === 0) return;

    const initial: ProductFetchResult[] = ids.map((id) => ({
      id,
      status: 'loading',
    }));
    setResults(initial);
    setIsFetching(true);

    await Promise.all(
      ids.map(async (id, index) => {
        try {
          const product = await fetchProductDataById(id);
          setResults((prev) => {
            const next = [...prev];
            next[index] = { id, status: 'success', product };
            return next;
          });
        } catch {
          setResults((prev) => {
            const next = [...prev];
            next[index] = { id, status: 'error', error: `Failed to load` };
            return next;
          });
        }
      })
    );

    setIsFetching(false);
  };

  const ids = parseIds(inputValue);
  const successCount = results.filter((r) => r.status === 'success').length;
  const errorCount = results.filter((r) => r.status === 'error').length;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Myntra Product Feed</h1>
        <p className={styles.description}>
          Enter Myntra product IDs (one per line or comma-separated) to generate
          a product feed.
        </p>

        <div className={styles.inputSection}>
          <textarea
            className={styles.textarea}
            placeholder={'123456\n789012\n345678'}
            value={inputValue}
            disabled={isFetching}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <div className={styles.inputFooter}>
            <span className={styles.idCount}>
              {ids.length > 0
                ? `${ids.length} ID${ids.length !== 1 ? 's' : ''}`
                : ''}
            </span>
            <button
              className={styles.generateButton}
              disabled={isFetching || ids.length === 0}
              onClick={handleGenerate}
            >
              {isFetching ? 'Fetching...' : 'Generate Feed'}
            </button>
          </div>
        </div>

        {results.length > 0 && !isFetching && (
          <div className={styles.summary}>
            {successCount} loaded
            {errorCount > 0 && `, ${errorCount} failed`}
          </div>
        )}
      </div>

      {results.length > 0 && (
        <div className={styles.feed}>
          {results.map((result) => {
            if (result.status === 'loading') {
              return <div key={result.id} className={styles.skeletonCard} />;
            }
            if (result.status === 'error') {
              return (
                <div key={result.id} className={styles.errorCard}>
                  <span className={styles.errorId}>ID: {result.id}</span>
                  <span className={styles.errorMsg}>{result.error}</span>
                </div>
              );
            }
            if (!result.product) return null;
            return (
              <div key={result.id} className={styles.cardWrapper}>
                <ProductCard product={result.product} />
                <span className={styles.productIdLabel}>
                  Product ID: {result.id}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductFeedPage;
